/**
 * src/api/client.js
 * Axios con auth por JWT (Bearer). El token maestro estático ya no vive en el
 * navegador: el panel se autentica con el JWT de sesión de un usuario admin.
 */
import axios from 'axios'
import { useAuthStore } from '../store/authStore.js'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const { token } = useAuthStore.getState()
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
      return Promise.reject(error)
    }
    if (!error.response) {
      // Dos averías distintas que se veían iguales (28-08): el timeout de 30 s
      // (el servidor existe pero tarda) y la petición sin respuesta ninguna
      // (caída, red, o un 500 sin cabeceras CORS que el navegador bloquea).
      const esTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')
      const networkErr = new Error(esTimeout
        ? 'El servidor no respondió en 30 s (se agotó la espera)'
        : 'La petición no obtuvo respuesta (servidor caído, red, o error bloqueado por el navegador)')
      networkErr.isNetworkError = true
      networkErr.isTimeout = esTimeout
      networkErr.original = error
      return Promise.reject(networkErr)
    }
    // 429: el backend cerró la puerta por intentos fallidos desde esta IP
    // (encargo de seguridad del 31-08). Sin este brazo saldría como error
    // genérico y parecería una avería del panel, que es justo lo contrario.
    if (error.response.status === 429) {
      const espera = error.response.headers?.['retry-after']
      const limitErr = new Error(
        error.response.data?.detail
        || `Demasiados intentos. Vuelve a intentarlo${espera ? ` en ${espera} s` : ' en unos minutos'}.`)
      limitErr.isRateLimited = true
      limitErr.status = 429
      return Promise.reject(limitErr)
    }
    if (error.response.status >= 500) {
      const serverErr = new Error(`Error del servidor (${error.response.status}). Inténtalo de nuevo.`)
      serverErr.isServerError = true
      serverErr.status = error.response.status
      serverErr.data = error.response.data
      return Promise.reject(serverErr)
    }
    return Promise.reject(error)
  }
)

export default api
